import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows, DataTableFilterMeta } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import referenceService from './refrence-service';
import { Toast } from 'primereact/toast';
import { Fieldset } from 'primereact/fieldset';
import { InputSwitch } from 'primereact/inputswitch';
import { Sidebar } from 'primereact/sidebar';
import { useTranslation } from 'react-i18next';
import BreadcrumbComponent from '../../../../components/Breadcrumb';

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    'fullName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    representative: { value: null, matchMode: FilterMatchMode.IN },
    date: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    balance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};

const ReferenceList: React.FC = () => {

    const [clientState, setClientState] = useState<any>(null);
    const { t, i18n } = useTranslation('common');
    const navigate = useNavigate();


    const [referenceGroup, setReferenceGroup] = useState<any[]>([]);
    const [referenceCode, setReferenceCode] = useState<Record<string, any[]>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
    const [referenceCodeInit, setReferenceCodeInit] = useState<any[] | DataTableExpandedRows>([]);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const toast = useRef<Toast>(null);
    const [referenceCodeItem, setReferenceCodeItem] = useState<any>(null);


    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await referenceService.getClientState();
                setClientState(clientStateItem);
            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    useEffect(() => {
        if (clientState) {
            const fetchUserList = async () => {
                try {
                    referenceService.findAllReferenceGroup().then((data) => {
                        setReferenceGroup(data);
                    });
                } catch (error) {
                    console.error('Error fetching user data:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchUserList();
        }
    }, [clientState]);

    useEffect(() => {
        const fetchReferenceCodes = async () => {
            if (typeof referenceCodeInit === 'object' && !Array.isArray(referenceCodeInit)) {
                for (const key of Object.keys(referenceCodeInit)) {
                    if (referenceCodeInit[key]) {
                        const [moduleId, referenceGroup] = key.split('-');

                        referenceService.findAllReferenceCode(moduleId, referenceGroup).then((data) => {
                            setReferenceCode((prev) => ({
                                ...prev,
                                [key]: data,
                            }));
                        });
                    }
                }
            }
        };

        fetchReferenceCodes();
    }, [referenceCodeInit]);



    function handleCreate(): void {
        navigate(`/admin/reference-edit`);
    }

    const handleEdit = (item: any) => {
        navigate('/admin/reference-edit', { state: { item: item } });
    };

    const handleView = (item: any) => {

        navigate('/admin/reference-view', { state: { item: item } });
    };


    const handleAdd = (item: any) => {
        const defaultRefCd = {
            moduleId: item.moduleId,
            referenceGroup: item.referenceGroup,
            fullDesc: '',
            shortDesc: '',
            recordStatus: true
        };

        setReferenceCodeItem(defaultRefCd);
        setSelectedItem(item); // Store the item if needed
        setIsPopupVisible(true); // Show the popup
    };

    const handleClosePopup = () => {
        setIsPopupVisible(false); // Hide the popup
        setSelectedItem(null); // Reset the item
    };

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setReferenceCodeItem({
            ...referenceCodeItem,
            [id]: value
        });
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters(defaultFilters);
        setGlobalFilterValue('');
    }

    const updatedReferenceGroup = referenceGroup.map(item => ({
        ...item,
        combinedKey: `${item.moduleId}-${item.referenceGroup}`, // Combine two fields
    }));

    const renderHeader = () => {
        return (
            <div className="flex justify-content-between">
                <div>
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                </div>
                <div>
                    <Button icon="pi pi-print" severity="danger" style={{ marginRight: '.1em' }} onClick={() => handlePrint()} />
                    <Button label="New" icon="pi pi-plus" style={{ marginRight: '.5em' }} onClick={() => handleCreate()} />
                </div>
            </div>
        );
    };

    const header = renderHeader();

    function handlePrint(): void {
        window.print();
    };

    const dialogFooter = () => {

        function submit(): void {
            const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

            referenceCodeItem.fullDescEnglish = referenceCodeItem.fullDesc;
            referenceCodeItem.insertDate = new Date(currentDate);
            referenceCodeItem.insertBy = clientState.userId;
            referenceCodeItem.modifiedDate = new Date(currentDate);
            referenceCodeItem.modifiedBy = clientState.userId;
            referenceCodeItem.recordStatus = referenceCodeItem.recordStatus == true ? '1' : '0';

            referenceService.addRefCd(referenceCodeItem).then(() => {
                clientState.success.detail = 'Already Save Record';
                toast.current?.show(clientState.success);
                handleClosePopup();

            }).catch((error) => {
                const errorMessage = error.response?.data?.message || 'Failed to create record.';
                clientState.error.detail = errorMessage;
                toast.current?.show(clientState.error);
            });

        }
        function handleBack(): void {
            handleClosePopup();
        }

        return (
            <>

                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Back" icon="pi pi-trash" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };

    const rowExpansionTemplate = (rowData: any) => {
        const combinedKey = `${rowData.moduleId}-${rowData.referenceGroup}`;

        return (
            <div className="orders-subtable">
                <DataTable value={referenceCode[combinedKey]} sortField='orderSeq' sortOrder={1}>
                    <Column
                        header="No."
                        style={{ width: '5%' }}
                        body={(_, { rowIndex }) => rowIndex + 1} // Render row number
                    ></Column>
                    <Column field="referenceCode" header="Reference Code"></Column>
                    <Column field="fullDesc" header="Reference Group Name"></Column>
                    <Column field="moduleId" header="Module Id"></Column>
                </DataTable>
            </div>
        );
    };


    const actionTemplate = (rowData: any) => (
        <div style={{ display: 'flex', gap: '5px' }}>
            <Button
                label="Edit"
                icon="pi pi-pencil"
                className="p-button-warning p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                tooltip="Edit"
                onClick={() => handleEdit(rowData)}
            />
            <Button
                label="Ref. Code"
                icon="pi pi-plus"
                className="p-button-secondary p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handleAdd(rowData)}
                tooltip="Add Reference Code"
                tooltipOptions={{ position: 'top' }}
            />
        </div>
    );

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Reference Group</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='card'>
                    <>
                        <Toast ref={toast} />
                        <Sidebar
                            visible={isPopupVisible}
                            onHide={() => setIsPopupVisible(false)}
                            baseZIndex={1000}
                            position="right"
                            style={{ width: '600px' }}
                            showCloseIcon={false}
                        >
                            <div className='col-12'>
                                <div className='form-title'>
                                    <Fieldset legend="Reference Code Form">
                                        <div className="p-fluid form-grid grid">
                                            <div className="field col-12 md:col-12">
                                                <label>Reference Group</label>
                                                <InputText
                                                    id="referenceGroup"
                                                    value={referenceCodeItem?.referenceGroup || ''}
                                                    onChange={handleInputChange}
                                                    disabled
                                                />
                                            </div>

                                            <div className="field col-12 md:col-12">
                                                <label>Reference Code</label>
                                                <InputText
                                                    id="referenceCode"
                                                    value={referenceCodeItem?.referenceCode || ''}
                                                    placeholder="Eg: M or F"
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Order Sequence</label>
                                                <InputText
                                                    id="orderSeq"
                                                    value={referenceCodeItem?.orderSeq || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <h6>Status</h6>
                                                <InputSwitch
                                                    checked={referenceCodeItem?.recordStatus || ''}
                                                    onChange={(e) => setReferenceCodeItem({ ...referenceCodeItem, recordStatus: e.value })}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Full Description</label>
                                                <InputText
                                                    id="fullDesc"
                                                    value={referenceCodeItem?.fullDesc || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Short Description</label>
                                                <InputText
                                                    id="shortDesc"
                                                    value={referenceCodeItem?.shortDesc || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Value1</label>
                                                <InputText
                                                    id="value1"
                                                    value={referenceCodeItem?.value1 || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Value2</label>
                                                <InputText
                                                    id="value2"
                                                    value={referenceCodeItem?.value2 || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Value3</label>
                                                <InputText
                                                    id="value3"
                                                    value={referenceCodeItem?.value3 || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Value4</label>
                                                <InputText
                                                    id="value4"
                                                    value={referenceCodeItem?.value4 || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Value5</label>
                                                <InputText
                                                    id="value5"
                                                    value={referenceCodeItem?.value5 || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="field col-12 md:col-12">
                                                <label>Value6</label>
                                                <InputText
                                                    id="value6"
                                                    value={referenceCodeItem?.value6 || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                    </Fieldset>
                                    <div>
                                        <br />
                                    </div>
                                    <Toolbar end={dialogFooter}></Toolbar>

                                </div>
                            </div>
                        </Sidebar>
                        <DataTable
                            value={updatedReferenceGroup}
                            showGridlines
                            scrollHeight="auto"
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            filters={filters}
                            globalFilterFields={['fullDesc', 'moduleId']}
                            header={header}
                            expandedRows={referenceCodeInit}
                            onRowToggle={(e) => setReferenceCodeInit(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                            dataKey="combinedKey"
                            sortField='modifiedDate'
                            sortOrder={-1}
                        >
                            <Column expander style={{ width: '3em' }} />
                            <Column
                                header="No."
                                style={{ width: '5%' }}
                                body={(_, { rowIndex }) => rowIndex + 1} // Render row number
                            ></Column>
                            <Column field="insertDate" header={t('register-date')} body={(rowData) => new Date(rowData.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })}></Column>
                            <Column field="fullDesc" header="Reference Group Name"></Column>
                            <Column field="referenceGroup" header="Reference Group"></Column>
                            <Column field="moduleId" header="Module Id"></Column>
                            <Column field="recordStatus" header="Status" body={(rowData) => rowData.recordStatus === '1' ? 'Active' : 'Inactive'}></Column>
                            <Column body={actionTemplate} header="Actions" style={{ width: '20%' }} ></Column>
                        </DataTable>
                    </>
                </div>
            </div>
        </div>
    );
};

export default ReferenceList;
